import React from 'react';
import GridLayout from 'react-grid-layout';
import { arrayMove } from 'react-movable';
import ContextMenu from './ContextMenu';
import Typography from '@mui/material/Typography';

export default class DraggableGridLayout extends React.Component {
    //#region React Hooks
    state = {
        "data": [],
        "originalData": [],
        "coordinates": [],
        "class": "",
        "columns": 4,
        "type": "horizontal",
        "rowHeight": 280,
        "width": 1000,
        "rightClick": false,
        "index": null,
        "tag": null,
        "slidesLoaded": 0,
        "forceUpdate": false,
        "commands": ["process slide"]
    };
    componentDidMount() {
        let identifier = (this.props.identifier) ? this.props.identifier : null;

        if (this.props.data) {
            this.buildGridData(this.props.data, identifier);
        }

        if (this.props.contextMenuCommands) {
            this.setState({ "commands": this.props.contextMenuCommands });
        }

        if (this.props.width) {
            this.setState({ "width": this.props.width });
        }

        if (this.props.slidesLoaded) {
            this.setState({ "slidesLoaded": this.props.slidesLoaded });
        }
    }
    componentDidUpdate(prevProps) {
        let identifier = (this.props.identifier) ? this.props.identifier : null;

        if (this.props.width !== this.state.width) {
            this.setState({ "width": this.props.width });
        }

        if (this.props.data.length !== this.state.data.length) {
            this.buildGridData(this.props.data, identifier)
        } else if (this.props.slidesLoaded !== this.state.slidesLoaded) {
            this.buildGridData(this.props.data, identifier)
            this.setState({ "slidesLoaded": this.props.slidesLoaded })
        }

        if (this.state.forceUpdate) {
            // We need this in order to assert all images not only loaded but the component properly updates
            setTimeout(() => {
                this.setState({ "forceUpdate": false }, () => {
                    this.buildGridData(this.props.data, identifier)
                })
            }, 500)
        }
    }
    render() {
        return (
            <>
                <GridLayout
                    className={this.state.class}
                    layout={this.state.data}
                    cols={this.state.columns}
                    compactType={this.state.type}
                    rowHeight={this.state.rowHeight}
                    width={this.state.width}
                    margin={[35, 15]}
                    isDraggable={!this.state.rightClick}
                    onLayoutChange={layout => {}}
                    onDragStop={(layout, oldItem, newItem) => {
                        let oldCoordinateIndex = -1, newCoordinateIndex = -1;

                        for (let i = 0; i < this.state.coordinates.length; i++) {
                            if (this.state.coordinates[i]["x"] === oldItem["x"] &&
                                this.state.coordinates[i]["y"] === oldItem["y"]) {
                                oldCoordinateIndex = i;
                            }

                            if (this.state.coordinates[i]["x"] === newItem["x"] &&
                                this.state.coordinates[i]["y"] === newItem["y"]) {
                                newCoordinateIndex = i;
                            }
                        }

                        let items = this.returnOrderOfItems(this.state.coordinates, this.state.data);
                        items = arrayMove(items, oldCoordinateIndex, newCoordinateIndex);
                        let newData = this.buildGridDataAfterDrag(items, this.state.coordinates);
                        this.setState({ "data": newData }, () => {
                            let updatedLayout = this.state.data;
                            let coordinates = this.state.coordinates;
                            this.verifyGridData(coordinates, updatedLayout);

                            let reorderedList = this.returnOrderOfItems(coordinates, updatedLayout);
                            if (this.props.onReorder) { this.props.onReorder(reorderedList); }
                        });
                    }}>
                    {this.state.data.map((item, index) => {
                        let originalData = this.state.originalData;
                        let lookup = originalData.find(d => d.tag === item.i);
                        let itemLookupKey = item.i + " " + this.state.slidesLoaded
                        return (<div key={item.i} className="tag-selection" onContextMenu={(e) => this.handleContextMenu(e, index, item.i)}>
                            <Typography variant="caption">{lookup.tag}</Typography>
                            <img className="slideImg" src={`data:image/jpeg;base64,${lookup["imageBytes"]}`} alt={itemLookupKey} />
                            <Typography variant="caption" title={lookup.description}>{lookup.description}</Typography>
                            {(this.state.rightClick && this.state.tag === item.i) ? <ContextMenu
                                id={item.i}
                                menuTemplate={this.state.commands}
                                deleteSlide={this.deleteSlide}
                                duplicateSlide={this.duplicateSlide}
                                addSlide={this.addSlide}
                            /> : null}
                        </div>);
                    })}
                    </GridLayout>
            </>
        );
    }
    //#endregion

    //#region Handle Events
    handleContextMenu = (event, index, tag) => {
        event.preventDefault();
        if (this.state.rightClick) {
            this.setState({ index: null, tag: null, rightClick: false })
        } else {
            this.setState({ index: index, tag: tag, rightClick: true })
        }
    }
    //#endregion

    //#region Data Processing
    buildGridData = (data, key) => {
        let builtData = [];
        let allAppropriateCoordinates = [];
        let columnMax = this.state.columns;
        let rowIterator = 0, columnIterator = 0;
        let anyPlaceholdersFound = false;
        const IMAGE_LOADING_BYTES = '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'

        data.forEach(d => {
            if (key) {
                builtData.push({ "i": d[key], "x": rowIterator, "y": columnIterator, "w": 1, "h": 1, "isBounded": true });
            } else {
                builtData.push({ "i": d, "x": rowIterator, "y": columnIterator, "w": 1, "h": 1, "isBounded": true });
            }
            allAppropriateCoordinates.push({ "x": rowIterator, "y": columnIterator });

            rowIterator = rowIterator + 1;

            if (rowIterator >= columnMax) {
                rowIterator = 0;
                columnIterator++;
            }

            if (d["imageBytes"] === IMAGE_LOADING_BYTES) { anyPlaceholdersFound = true }
        });

        this.setState({ "data": builtData, "originalData": data, "coordinates": allAppropriateCoordinates }, () => {
            if (anyPlaceholdersFound) {
                this.setState({ "forceUpdate": true })
            }
        });
    };
    buildGridDataAfterDrag = (data, coordinates) => {
        let builtData = [];
        for (let i = 0; i < data.length; i++) {
            builtData.push({ "i": data[i], "x": coordinates[i]["x"], "y": coordinates[i]["y"], "w": 1, "h": 1, "isBounded": true });
        }
        return builtData
    };
    // Because this plugin does not allow me to keep a strict grid layout, we need this written in order to enforce the layout
    verifyGridData = (coordinates, currentLayout) => {
        let decoupledLookups = [];
        coordinates.forEach(c => {
            let lookup = currentLayout.find(l => l["x"] === c["x"] && l["y"] === c["y"]);
            if (!lookup) {
                // Didn't Find Lookup
                decoupledLookups.push(c);
            }
        });

        // Find the lookup containers that went out of bound and reset them for one of the decoupled lookups
        if (decoupledLookups.length > 0) {
            currentLayout.forEach(c => {
                let lookup = coordinates.find(l => l["x"] === c["x"] && l["y"] === c["y"]);
                if (!lookup) {
                    let reset = decoupledLookups.pop();
                    c["x"] = reset["x"];
                    c["y"] = reset["y"];
                }
            });
        }
    };
    returnOrderOfItems = (coordinates, currentLayout) => {
        let rtnVal = [];

        coordinates.forEach(c => {
            let lookup = currentLayout.find(l => l["x"] === c["x"] && l["y"] === c["y"]);
            rtnVal.push(lookup["i"]);
        });

        return rtnVal;
    };
    deleteSlide = () => {
        if (this.props.onDelete) {
            this.props.onDelete(this.state.tag);
        }
        this.setState({ index: null, tag: null, rightClick: false });
    };
    duplicateSlide = () => {
        if (this.props.onDuplicate) {
            this.props.onDuplicate(this.state.index);
        }
        this.setState({ index: null, tag: null, rightClick: false });
    };
    addSlide = () => {
        if (this.props.onAdd) {
            this.props.onAdd(this.state.tag);
        }
        this.setState({ index: null, tag: null, rightClick: false });
    };
    processSlide = () => {
        this.setState({ index: null, tag: null, rightClick: false });
    };
    //#endregion
}